import type { TReachabilityContext } from './TargetAudienceOverview.tsx';
import type { IAudienceStatsReturn } from './TargetAudienceOverview.useAudienceStats';

export interface ITextMapConfig {
  [key: string]: {
    wizard: string;
    report: string;
  };
}

export interface INumberMapConfig {
  [key: string]: {
    wizard: number;
    report: number;
    // Optional format string for this number
    format?: string;
  };
}

interface IStatsProps
  extends Pick<
    IAudienceStatsReturn,
    | 'numReachedUsers'
    | 'numReachedEmails'
    | 'numReachedLookalike'
    | 'numReachedCookies'
    | 'numNewDevices'
    | 'numReachedDevices'
    | 'numReachedMeta'
    | 'numReachedMetaLookalike'
  > {}

/**
 * Text mapping configuration for TargetAudienceOverview component.
 * Maps text keys to their wizard and report context variants.
 * This is now defined inside the getText function to allow proper translation.
 */

/**
 * Number mapping configuration for TargetAudienceOverview component.
 * Maps number keys to their wizard and report context variants.
 */
export function createNumberMap(
  numTargetedReachableUsers: number,
  numTargetedReachableEmails: number,
  targetedLookalikeAudienceSize: number,
  actualLookalikeAudienceSize: number,
  numTargetedReachableCookies: number,
  numTargetedReachableDevices: number,
  numNewDevices: number,
  numDevicesPerPerson: number,
  statsProps?: IStatsProps,
  lookalikeAudienceSize?: number,
  numTargetedMeta?: number,
  numTargetedMetaLookalike?: number,
): INumberMapConfig {
  // Use the local audienceSize if provided (for immediate updates), otherwise use API value
  const lookalikeSize = lookalikeAudienceSize ?? 0;

  return {
    reachedUsers: {
      // In wizard, show targeted (no one reached yet)
      wizard: numTargetedReachableUsers + lookalikeSize,
      // In report, show actual reached
      report: statsProps ? statsProps.numReachedUsers : 0,
      format: '0,0',
    },
    targetedUsers: {
      wizard: numTargetedReachableUsers,
      report: numTargetedReachableUsers + actualLookalikeAudienceSize,
      format: '0,0',
    },
    reachedEmails: {
      // In wizard, no one reached yet
      wizard: 0,
      // In report, show actual reached
      report: statsProps ? statsProps.numReachedEmails : 0,
      format: '0,0',
    },
    targetedEmails: {
      wizard: numTargetedReachableEmails,
      report: numTargetedReachableEmails,
      format: '0,0',
    },
    reachedCookies: {
      // In wizard, no one reached yet
      wizard: 0,
      // In report, show actual reached
      report: statsProps ? statsProps.numReachedCookies : 0,
      format: '0,0',
    },
    targetedCookies: {
      wizard: numTargetedReachableCookies,
      report: numTargetedReachableCookies,
      format: '0,0',
    },
    reachedDevices: {
      // In wizard, show targeted (no one reached yet)
      wizard: numTargetedReachableDevices,
      // In report, show actual reached
      report: statsProps ? statsProps.numReachedDevices : 0,
      format: '0,0',
    },
    newDevices: {
      wizard: numNewDevices,
      report: numNewDevices,
      format: '0,0',
    },
    devicesPerPerson: {
      wizard: numDevicesPerPerson,
      report: numDevicesPerPerson,
      format: '0.00',
    },
    // Percentage calculations
    reachedUsersPercent: {
      wizard: 1,
      // Clamp the percentage to 100%
      report: statsProps
        ? Math.min(
            statsProps.numReachedUsers / (numTargetedReachableUsers + actualLookalikeAudienceSize),
            1,
          )
        : 1,
      format: '0.00%',
    },
    // Chart data (raw numbers, not formatted)
    chartBlueCount: {
      // In wizard, show targeted
      wizard: numNewDevices,
      // In report, show actual reached
      report: statsProps ? statsProps.numNewDevices : 0,
      // No formatting for chart data
      format: '0',
    },
    chartTealCount: {
      wizard: numTargetedReachableEmails,
      report: statsProps ? statsProps.numReachedEmails : 0,
      // No formatting for chart data
      format: '0',
    },
    lookalikeUsers: {
      wizard: lookalikeSize,
      report: statsProps ? statsProps.numReachedLookalike : 0,
      format: '0,0',
    },
    lookalikeActualUsers: {
      wizard: 0,
      report: actualLookalikeAudienceSize,
      format: '0,0',
    },
    chartYellowCount: {
      wizard: lookalikeSize,
      report: statsProps ? statsProps.numReachedLookalike : 0,
      format: '0',
    },
    targetedMeta: {
      wizard: numTargetedMeta ?? 0,
      report: numTargetedMeta ?? 0,
      format: '0,0',
    },
    reachedMeta: {
      wizard: 0,
      report: statsProps?.numReachedMeta ?? 0,
      format: '0,0',
    },
    chartMetaCount: {
      wizard: numTargetedMeta ?? 0,
      report: statsProps?.numReachedMeta ?? 0,
      format: '0',
    },
    targetedMetaLookalike: {
      wizard: numTargetedMetaLookalike ?? 0,
      report: numTargetedMetaLookalike ?? 0,
      format: '0,0',
    },
    reachedMetaLookalike: {
      wizard: 0,
      report: statsProps?.numReachedMetaLookalike ?? 0,
      format: '0,0',
    },
  };
}

/**
 * Simple utility function to get all context-appropriate texts
 * @param t - Translation function
 * @param context - Current context (wizard or report)
 * @returns Object with all translated texts for the given context
 */
export function getText(
  t: (key: string, params?: Record<string, unknown>) => string,
  context: TReachabilityContext,
): Record<string, string> {
  // Define text map inside function so t() can wrap each string literal
  const targetAudienceTextMap: ITextMapConfig = {
    cardTitle: {
      wizard: t('Reachability Breakdown'),
      report: t('Reach Breakdown'),
    },
    uniquePeopleLabel: {
      wizard: t('Unique reachable people'),
      report: t('Unique people reached'),
    },
    displaySectionLabel: {
      wizard: t('Display'),
      report: t('Display'),
    },
    metaSectionLabel: {
      wizard: t('Meta'),
      report: t('Meta'),
    },
    devicesReachLabel: {
      wizard: t('Device Reach'),
      report: t('Device Reach'),
    },
    devicesLabel: {
      wizard: t('Targetable Devices'),
      report: t('Devices Reached'),
    },
    devicesTooltip: {
      wizard: t(
        'Total devices based on unique reachable people. Devices include mobile, tablet, and desktop.',
      ),
      report: t(
        'Total devices that received ads during the campaign period. Devices include mobile, tablet, and desktop.',
      ),
    },
    uniquePeopleTooltip: {
      wizard: t('Unique individuals we can reach via any targeting method.'),
      report: t('Unique individuals who were served ads during the campaign period.'),
    },
    emailTooltip: {
      wizard: t('People we can reach through email mapping.'),
      report: t('People reached through email mapping.'),
    },
    cookieTooltip: {
      wizard: t('People we can reach through browser cookies and mobile advertising IDs.'),
      report: t('People reached through browser cookies and mobile advertising IDs.'),
    },
    newDevicesTooltip: {
      wizard: t('Estimated additional devices we can reach.'),
      report: t('Reached devices (previously unknown).'),
    },
    emailLabel: {
      wizard: t('Email Addresses'),
      report: t('Email Addresses'),
    },
    cookieLabel: {
      wizard: t('Device targeting'),
      report: t('Device targeting'),
    },
    newDevicesLabel: {
      wizard: t('New Devices'),
      report: t('New Devices'),
    },
    additionalText: {
      wizard: t('Some people are reachable in multiple ways'),
      report: t('Some people are reachable in multiple ways'),
    },
    emptyStateLabel: {
      wizard: t('No audience data available'),
      report: t('No audience data available'),
    },
    lookalikeLabel: {
      wizard: t('Lookalike expansion'),
      report: t('Lookalike expansion'),
    },
    lookalikeTooltip: {
      wizard: t('Relative size of the lookalike audience targeted.'),
      report: t('People reached using a relative size of lookalike audience targeting.'),
    },
    metaLabel: {
      wizard: t('Your audience'),
      report: t('Your audience'),
    },
    metaTooltip: {
      wizard: t('People we can reach through Meta advertising.'),
      report: t('People reached through Meta advertising.'),
    },
    metaLookalikeLabel: {
      wizard: t('Lookalike expansion'),
      report: t('Lookalike expansion'),
    },
    metaLookalikeTooltip: {
      wizard: t('People we can reach through Meta lookalike audience targeting.'),
      report: t('People reached through Meta lookalike audience targeting.'),
    },
  };

  const texts: Record<string, string> = {};

  for (const key of Object.keys(targetAudienceTextMap)) {
    const mapping = targetAudienceTextMap[key];
    if (!mapping) {
      // eslint-disable-next-line no-console
      console.warn(`Text mapping not found for key: ${key}`);
      texts[key] = key;
      continue;
    }

    const text = mapping[context];
    if (!text) {
      // eslint-disable-next-line no-console
      console.warn(`Text mapping not found for key: ${key} in context: ${context}`);
      texts[key] = key;
      continue;
    }

    texts[key] = text;
  }

  return texts;
}

/**
 * Simple utility function to get all context-appropriate numbers
 * @param numberMap - The number mapping configuration
 * @param context - Current context (wizard or report)
 * @returns Object with all numbers for the given context
 */
export function getNumbers(
  numberMap: INumberMapConfig,
  context: TReachabilityContext,
): Record<string, number> {
  const numbers: Record<string, number> = {};

  for (const key of Object.keys(numberMap)) {
    const mapping = numberMap[key];
    if (!mapping) {
      // eslint-disable-next-line no-console
      console.warn(`Number mapping not found for key: ${key}`);
      numbers[key] = 0;
      continue;
    }

    const number = mapping[context];
    if (number === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`Number mapping not found for key: ${key} in context: ${context}`);
      numbers[key] = 0;
      continue;
    }

    numbers[key] = number;
  }

  return numbers;
}

/**
 * Simple utility function to get all context-appropriate formatted numbers
 * @param numberMap - The number mapping configuration
 * @param context - Current context (wizard or report)
 * @returns Object with all formatted number strings for the given context
 */
export function getFormattedNumbers(
  numberMap: INumberMapConfig,
  context: TReachabilityContext,
): Record<string, string> {
  const formattedNumbers: Record<string, string> = {};

  for (const key of Object.keys(numberMap)) {
    const mapping = numberMap[key];
    if (!mapping) {
      // eslint-disable-next-line no-console
      console.warn(`Number mapping not found for key: ${key}`);
      formattedNumbers[key] = '0';
      continue;
    }

    const number = mapping[context];
    if (number === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`Number mapping not found for key: ${key} in context: ${context}`);
      formattedNumbers[key] = '0';
      continue;
    }

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const numeral = require('numeral');
    // Use format from the map, default to '0,0'
    const format = mapping.format || '0,0';
    formattedNumbers[key] = numeral(number).format(format);
  }

  return formattedNumbers;
}

/**
 * Simple utility function to get all context-appropriate raw numbers (for charts, etc.)
 * @param numberMap - The number mapping configuration
 * @param context - Current context (wizard or report)
 * @returns Object with all raw numbers for the given context
 */
export function getRawNumbers(
  numberMap: INumberMapConfig,
  context: TReachabilityContext,
): Record<string, number> {
  const rawNumbers: Record<string, number> = {};

  for (const key of Object.keys(numberMap)) {
    const mapping = numberMap[key];
    if (!mapping) {
      // eslint-disable-next-line no-console
      console.warn(`Number mapping not found for key: ${key}`);
      rawNumbers[key] = 0;
      continue;
    }

    const number = mapping[context];
    if (number === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`Number mapping not found for key: ${key} in context: ${context}`);
      rawNumbers[key] = 0;
      continue;
    }

    rawNumbers[key] = number;
  }

  return rawNumbers;
}

export default {};
